import { useRef, useState } from 'react';
import { Play } from 'lucide-react';
import { useMediaQuery } from './useReducedMotion';
import { track } from '../track';

// ── Video slot ─────────────────────────────────────────────────────────────────────────────
// Vela's introduction, supplied by Veluro. Swap these URLs to connect a different cut.
// Posters are real frames from each cut (public/vela/*) so visitors meet Vela before pressing play.
const VIDEO = {
  desktop: { // 16:9 · 1920×1080
    src: 'https://customer-assets-0z36b82j.emergentagent.net/job_e93ada9d-3aef-47b1-a2df-1d6ae15654cb/artifacts/7t4wd0md_vela_desktop_16x9_web.mp4.mp4',
    poster: '/vela/intro-poster-16x9.webp', ratio: 'aspect-video',
  },
  mobile: { // 9:16 · 1080×1920
    src: 'https://customer-assets-0z36b82j.emergentagent.net/job_e93ada9d-3aef-47b1-a2df-1d6ae15654cb/artifacts/qgyma0x2_vela_mobile_9x16_web.mp4.mp4',
    poster: '/vela/intro-poster-9x16.webp', ratio: 'aspect-[9/16]',
  },
};
// Same voice track on both cuts — one caption file (WebVTT), on by default so it reads well with the sound off.
const CAPTIONS = '/vela/intro-captions.vtt';

// Poster + "Watch Vela's introduction" → the clip loads only on click, plays inline with native controls (incl. fullscreen). Never autoplays.
export function VelaIntroVideo({ className = '' }) {
  const desktop = useMediaQuery('(min-width: 768px)');
  const cut = desktop ? VIDEO.desktop : VIDEO.mobile;
  const ref = useRef(null);
  const [started, setStarted] = useState(false);

  const play = async () => {
    const v = ref.current;
    if (!v) return;
    if (!v.getAttribute('src')) { v.setAttribute('src', cut.src); v.load(); }
    setStarted(true);
    track('meet_vela_video_played', { cut: desktop ? 'desktop' : 'mobile' });
    try { await v.play(); } catch { /* the native controls are visible — the visitor can press play */ }
  };

  return (
    <div className={`relative ${className}`} data-testid="meet-vela-video">
      <div aria-hidden="true" className="absolute -inset-6 sm:-inset-10 rounded-[2.5rem] bg-[radial-gradient(ellipse_at_center,rgba(139,92,246,0.28),rgba(217,70,239,0.12)_45%,transparent_72%)] blur-2xl pointer-events-none" />
      <div className={`relative ${desktop ? '' : 'flex justify-center'}`}>
      <div className={`relative ${cut.ratio} ${desktop ? 'w-full' : 'h-[62vh] max-h-[520px] min-h-[360px]'} rounded-2xl sm:rounded-3xl overflow-hidden border border-white/10 ring-1 ring-white/[0.04] bg-[#0a0a12] shadow-[0_40px_120px_-30px_rgba(139,92,246,0.45)]`}>
        <video ref={ref} poster={cut.poster} preload="none" playsInline controls={started} controlsList="nodownload"
          onEnded={() => track('meet_vela_video_completed')}
          aria-label="Vela’s introduction video" data-testid="meet-vela-video-el"
          className="absolute inset-0 h-full w-full object-contain bg-[#0a0a12]">
          <track kind="captions" src={CAPTIONS} srcLang="en" label="English" default data-testid="meet-vela-captions" />
        </video>
        {!started && (
          <button type="button" onClick={play} data-testid="meet-vela-play"
            aria-label="Watch Vela’s introduction"
            className="group absolute inset-0 flex flex-col items-center justify-end sm:justify-center pb-6 sm:pb-0 focus:outline-none">
            <span className="absolute inset-0 bg-gradient-to-t from-[#07070b]/70 via-transparent to-transparent sm:bg-none transition-colors group-hover:bg-black/10" aria-hidden="true" />
            <span className="relative inline-flex items-center gap-3 rounded-full pl-1.5 pr-5 py-1.5 bg-[#0d0d16]/80 backdrop-blur-xl border border-white/15 shadow-2xl shadow-violet-900/40 transition-[transform,border-color,box-shadow] group-hover:scale-[1.03] group-hover:border-fuchsia-400/50 group-focus-visible:ring-2 group-focus-visible:ring-fuchsia-400/80 group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-[#07070b]">
              <span className="relative flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-lg shadow-fuchsia-500/30">
                <Play className="h-5 w-5 translate-x-[1px]" fill="currentColor" />
              </span>
              <span className="text-sm font-semibold text-white tracking-tight">Watch Vela’s introduction</span>
            </span>
          </button>
        )}
      </div>
      </div>
    </div>
  );
}
